import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import T from '../../i18n/i18n';
import { assetsActions, rtcBufferActions } from '../../../redux/actions';
import { editAssetContent, getAssetsContent } from '../../../services/assets';
import { Window, Header, HeaderTitle, ButtonBar, ButtonSide, Button, Background, Content, UploadInput, ConfirmMessage } from './AssetViewer.styles';
import Keyboard from '../../../services/keyboardShortcuts';
import TextEditor from '../../Forms/TextEditor/TextEditor';
import ImageViewer from '../../Forms/ImageViewer/ImageViewer';

export default ({tableId}) => {
    
    const dispatch = useDispatch();
    const [content, setContent] = useState(null);
    const [loadedContent, setLoadedContent] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false)

    const opened = useSelector(state => state.assetsState.opened);
    const asset = useSelector(state => state.assetsState.asset);
    const contentUpdate = useSelector(state => state.assetsState.contentUpdate);
    const isOwner = useSelector(state => state.authState.isOwner);

    const resourceRef = useRef();

    const getContent = useCallback(async (id, isOpened) => {
        if (id && isOpened) {
            try {
                const res = await getAssetsContent(tableId, id);
                if (res && res.payload) {
                    setContent(JSON.parse(res.payload));
                    setLoadedContent(res.payload);
                }
            } catch (e) {
                console.error(Object.assign(e,{component:'AssetViewer', id: 'getContent'}));
            }
        }
    }, [tableId]);

    useEffect(() => {
        if (!contentUpdate) {
            return;
        }

        try {
            const ctn = JSON.parse(contentUpdate);
            if (ctn.id === asset.id) {
                setContent(ctn.content);
                setLoadedContent(ctn.content);
                dispatch(assetsActions.updateContent(null));
            }
        } catch (e) {
            console.error(Object.assign(e,{component:'AssetViewer', id: 'parseContentUpdate'}));
        }
    }, [contentUpdate, dispatch, asset]);

    useEffect(() => {
        if (!opened || !asset) {
            return;
        }
        
        getContent(asset.id, opened);

        let escapeKey = () => {
            dispatch(assetsActions.closeWindow());
            return false;
        };
        
        Keyboard.register({key:'ESCAPE'}, escapeKey);

        return () => {
            Keyboard.unregister({key:'ESCAPE'}, escapeKey);
        }
    }, [opened, asset, dispatch, getContent]);


    const close = () => {
        dispatch(assetsActions.closeWindow());
    }

    const saveContent = async () => {
        const ctn = typeof content === 'string' ? content : JSON.stringify(content);
        await editAssetContent(tableId, asset.id, {content:ctn});
        close();

        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'assetContentUpdate',
            data: JSON.stringify({id: asset.id, content:ctn})
        }));
    }

    const keyCancel = (e) => {
        e.stopPropagation();
    }

    const showToPlayers = (e) => {
        dispatch(rtcBufferActions.pushOutputBuffer({
            type: 'assetShow',
            data: JSON.stringify(asset)
        }));
    }

    const onChangeContent = useCallback((v) => {
        setContent(v);
    }, []);

    const hookRefFromChild = (r) => {
        resourceRef.current = r.current;
    }

    return <>
        <Background onKeyDown={keyCancel} className={opened ? 'opened' : ''} onClick={(e) => {close()}} />
        <Window onKeyDown={keyCancel} className={opened ? 'opened' : ''}>
            <Header>
                <HeaderTitle><T value="assets.viewerTitle"/></HeaderTitle>
            </Header>

            <Content>
                {asset && asset.type === 0 ?
                    <TextEditor 
                        readOnly={!isOwner}
                        onChange={onChangeContent}
                        value={loadedContent}
                    />
                : null }

                {asset && asset.type === 1 ?
                    <ImageViewer 
                        showFileList={isOwner}
                        onChange={onChangeContent}
                        value={loadedContent}
                        onRef={hookRefFromChild}
                    />
                : null }
            </Content>
            
            <ButtonBar>
                <ButtonSide>
                    {isOwner ? <>
                        <UploadInput accept="image/png,image/jpeg" onChange={(e) => {setConfirmRemove(false); resourceRef.current.handleFileLoading(e.target.files)}} name="asset-upload" id="asset-upload" />
                        {asset && asset.type === 1 ? <>
                            {confirmRemove ? <>
                                {content && content.length > 0 ? <>
                                    <ConfirmMessage><T value="filemanager.confirmRemove" /></ConfirmMessage>
                                    <Button color={"#0c0"} onClick={() => {setConfirmRemove(false)}}><T value="no" /></Button>
                                    <Button color={"#f44"} onClick={async () => {await resourceRef.current.removeSelectedFiles(); setConfirmRemove(false)}}><T value="yes" /></Button>
                                </> : null}
                            </> : <>
                                <Button color={"#39f"} htmlFor="asset-upload"><T value="filemanager.upload" /></Button>
                                {content && content.length > 0 && <Button color={"#f44"} onClick={() => {setConfirmRemove(true)}}><T value="delete" /></Button>}
                                <Button color={"#0c0"} onClick={(e) => {showToPlayers()}}><T value="assets.showToPlayers" /></Button>
                            </>}
                        </> : <Button color={"#0c0"} onClick={(e) => {showToPlayers()}}><T value="assets.showToPlayers" /></Button>}
                    </> : null }
                </ButtonSide>
                <ButtonSide>
                    {isOwner ? 
                        <Button color={"#39f"} onClick={(e) => {saveContent()}}><T value="save" /></Button>
                    : null }
                    <Button color={"#999"} onClick={(e) => {close()}}><T value="cancel" /></Button>
                </ButtonSide>

            </ButtonBar>
        </Window>
    </>;

};